import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { decodeParam, getLocalStorage, loggedInUser } from "../helpers/common";
import { USER_INFO_KEY } from "../constants/common";
import { loginOrSignUp } from "../helpers/message";
import { getIntroductionDetaisById } from "../services/introductionService";
import { getDirectChats } from "../services/messagesService";
import MessagesRightSection from "../components/Messages/MessagesRightSection";
import DirectMessagesRightSection from "../components/Messages/DirectMessagesRightSection";
import IntroDetails from "../components/Messages/IntroDetails";
import AgentProfileViewModal from "../components/Introduction/AgentProfileViewModal";
import InboxList, { ThreadPlaceholder } from "@/components/Inbox";
import useFitHeight from "@/components/Inbox/useFitHeight";
import InboxThread from "@/components/InboxThread";
import { buildRows, filterRows, sortRows } from "@/components/Inbox/inboxModel";
import { pageURLs } from "@/constants";
import { PageWrapper } from "@/components/PageWrapper";
import { useMLSAgentAtGitchaHomeRedirect } from "@/hooks/useMLSAgentAtGitchaRedirect";

const INTRO_DETAILS_KEY = "introductionDetails";
const DIRECT_CHATS_KEY = "directChats";

const Messaging = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const userId = _.get(getLocalStorage(USER_INFO_KEY), "userInfo.userId", "");
  const userDetails = loggedInUser();
  const inboxRef = useFitHeight();

  const isDirectRoute = (location.pathname || "").startsWith(pageURLs.directMessages);
  const dmId = isDirectRoute ? searchParams.get("id") : null;
  const introFromUrl = !isDirectRoute && searchParams.get("Id") ? decodeParam(searchParams.get("Id")) : null;

  const [introId, setIntroId] = useState(location.state?.introId ?? introFromUrl ?? null);
  const [filter, setFilter] = useState("all");
  const [term, setTerm] = useState("");
  const [userDeleted, setUserDeleted] = useState(false);
  const [introDetailsRequested, setIntroDetailsRequested] = useState(false);
  const [introDetailsId, setIntroDetailsId] = useState(null);
  const [showProfileModal, setProfileModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(0);
  const [isFromMessage, setIsFromMessage] = useState(false);

  const chatMessages = useSelector((state) => state.unreadCount.chatMessages);
  const unreadDmChatIds = useSelector((state) => state.unreadCount.unreadDmChatIds) || [];

  useEffect(() => {
    const login = loginOrSignUp(_.get(userDetails, "userInfo.email", null));
    if (!login) navigate(pageURLs.home);
  }, []);

  useEffect(() => {
    if (location.state?.introId) setIntroId(location.state.introId);
  }, [location.state?.introId]);

  // A link to another chat while Messaging is already mounted (bell click)
  // changes only the URL; the useState initializer above has already run.
  useEffect(() => {
    if (introFromUrl) setIntroId(introFromUrl);
  }, [introFromUrl]);

  const directChatsKey = [DIRECT_CHATS_KEY, userId, dmId];
  const directChatsQuery = useQuery({
    queryKey: directChatsKey,
    queryFn: async () => {
      const response = await getDirectChats();
      if (response?.errorCode !== 0) {
        throw new Error(response?.message || "Failed to load direct messages");
      }
      const result = _.get(response, "result", null);
      return Array.isArray(result) ? result : [];
    },
    enabled: !!userId,
    staleTime: 0,
    gcTime: 0,
  });
  const lastLoadedChats = useRef([]);
  if (directChatsQuery.data) lastLoadedChats.current = directChatsQuery.data;
  const directChats = directChatsQuery.data ?? lastLoadedChats.current;

  const unreadDmKey = unreadDmChatIds.join(",");
  const firstUnreadSync = useRef(true);
  useEffect(() => {
    if (firstUnreadSync.current) {
      firstUnreadSync.current = false;
      return;
    }
    queryClient.invalidateQueries({ queryKey: [DIRECT_CHATS_KEY] });
  }, [unreadDmKey]);

  const updateLastMessage = (conversationId, lastMessage) => {
    if (!directChats.some((item) => item.id === conversationId)) {
      queryClient.invalidateQueries({ queryKey: [DIRECT_CHATS_KEY] });
      return;
    }
    queryClient.setQueryData(directChatsKey, (prev) =>
      (prev ?? directChats).map((item) =>
        item.id === conversationId ? { ...item, lastMessage, lastMessageAt: new Date().getTime() } : item,
      ),
    );
  };

  const lastOrderedRef = useRef([]);
  const allRows = useMemo(() => {
    const rows = buildRows({ intros: chatMessages?.payload, chats: directChats, me: userId, unreadIds: unreadDmChatIds });
    if (rows.some((row) => row.pending)) {
      const byKey = Object.fromEntries(rows.map((row) => [row.key, row]));
      const held = lastOrderedRef.current.filter((row) => byKey[row.key]).map((row) => byKey[row.key]);
      const added = rows.filter((row) => !held.some((h) => h.key === row.key));
      return [...added, ...held];
    }
    const ordered = sortRows(rows);
    lastOrderedRef.current = ordered;
    return ordered;
  }, [chatMessages, directChats, userId, unreadDmKey]);

  const rows = useMemo(() => filterRows(allRows, filter, term), [allRows, filter, term]);
  const unreadCount = allRows.filter((row) => row.unread).length;

  const selectedKey = dmId ? `dm-${dmId}` : introId ? `intro-${introId}` : null;

  const selectedIntro = useMemo(() => {
    if (dmId || !introId) return null;
    return (chatMessages?.payload || []).find((item) => String(item.introductionId) === String(introId)) || null;
  }, [chatMessages, introId, dmId]);

  const selectRow = (row) => {
    if (row.kind === "direct") {
      setIntroId(null);
      navigate(`${pageURLs.directMessages}?id=${row.id}`);
      return;
    }
    setIntroId(row.id);
    if (isDirectRoute || searchParams.get("Id")) navigate(pageURLs.messages, { state: { introId: row.id } });
  };

  const backToList = () => {
    setIntroId(null);
    if (isDirectRoute) navigate(pageURLs.directMessages, { replace: true });
  };

  const introDetailsQuery = useQuery({
    queryKey: [INTRO_DETAILS_KEY, introDetailsId],
    queryFn: async () => {
      const response = await getIntroductionDetaisById(introDetailsId);
      const responseResult = _.get(response, "result", null);
      if (response?.errorCode !== 0 || !responseResult) {
        throw new Error(response?.message || "Could not load the introduction details");
      }
      return responseResult[0];
    },
    enabled: !!introDetailsId,
  });
  const introDetails = introDetailsQuery.data ?? [];
  const showViewIntroductionModal = introDetailsRequested && !!introDetailsQuery.data;

  const getIntroductionDetails = (id) => {
    if (!id) return;
    setIntroDetailsId(id);
    setIntroDetailsRequested(true);
  };

  const viewProfile = (agentId, fromMessageScreen = false) => {
    setIntroDetailsRequested(false);
    setIsFromMessage(fromMessageScreen);
    setSelectedUserId(agentId);
    setProfileModal(true);
  };

  const closeAgentModal = (fromMessageScreen) => {
    if (!fromMessageScreen) setIntroDetailsRequested(true);
    setProfileModal(false);
  };

  useMLSAgentAtGitchaHomeRedirect();

  const threadOpen = !!(dmId || selectedIntro);
  const loading = !chatMessages?.payload && directChatsQuery.isLoading;

  return (
    <>
      <PageWrapper>
        <div className="main-content p-0 agent-no-login-content">
          <div ref={inboxRef} className={`inbox${threadOpen ? " is-thread-open" : ""}`}>
            <InboxList
              rows={rows}
              selectedKey={selectedKey}
              onSelect={selectRow}
              filter={filter}
              onFilter={setFilter}
              term={term}
              onTerm={setTerm}
              unreadCount={unreadCount}
              loading={loading}
              onBrowse={() => navigate(pageURLs.home)}
            />
            <InboxThread>
              {dmId ? (
                <DirectMessagesRightSection
                  key={`dm-${dmId}`}
                  chatSource={directChats.find((chat) => String(chat.userId) === String(dmId))?.source}
                  updateLastMessage={updateLastMessage}
                  userId={userId}
                  userDeletedUpdate={setUserDeleted}
                  onMobileBack={backToList}
                />
              ) : selectedIntro ? (
                <MessagesRightSection
                  selectedId={selectedIntro.introductionId}
                  userId={userId}
                  selectedData={selectedIntro}
                  getIntroductionDetails={getIntroductionDetails}
                  ViewProfileModal={(agentId) => viewProfile(agentId, true)}
                  userDeletedUpdate={setUserDeleted}
                  introductionsArray={[]}
                  fromDM={false}
                  onMobileBack={backToList}
                />
              ) : (
                <ThreadPlaceholder />
              )}
            </InboxThread>
          </div>
        </div>
      </PageWrapper>
      <IntroDetails
        details={introDetails}
        show={showViewIntroductionModal}
        onHide={(state) => setIntroDetailsRequested(!!state)}
        ViewProfileModal={(agentId) => viewProfile(agentId)}
        userDeleted={userDeleted}
      />
      <AgentProfileViewModal
        show={showProfileModal}
        onHide={() => setProfileModal(false)}
        userId={selectedUserId}
        isFromMessage={isFromMessage}
        onClose={closeAgentModal}
      />
    </>
  );
};

export default Messaging;
